import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, PlayCircle, Clock, Building2 } from "lucide-react";
import SiteFooter from "@/components/site/SiteFooter";

const HERO =
  "https://images.unsplash.com/photo-1714974528693-f77f6fcc56af?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function Training() {
  const { t } = useLang();
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="glass-panel sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-md bg-slate-950 text-white flex items-center justify-center">
              <Building2 className="h-5 w-5" />
            </div>
            <div className="font-heading font-bold text-slate-950">Rana Associates</div>
          </Link>
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2" data-testid="training-back-home">
              <ArrowLeft className="h-4 w-4" /> Home
            </Button>
          </Link>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO})` }} />
        <div className="absolute inset-0 bg-slate-950/75" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-300">Free · Open Access</p>
          <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-bold text-white tracking-tight">
            {t("training_courses")}
          </h1>
          <p className="mt-4 text-slate-200 max-w-2xl">
            Sharpen your valuation skills — free courses on bylaws, boundary measurement, KYC, and bank
            submission workflows.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {courses.length === 0 ? (
          <p className="text-slate-500">No courses published yet. Please check back soon.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((c) => (
              <Card key={c.id} className="border border-slate-200 hover:shadow-md transition-shadow" data-testid={`course-card-${c.id}`}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <PlayCircle className="h-8 w-8 text-blue-600" />
                    {c.duration_min && (
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <Clock className="h-3 w-3" /> {c.duration_min} min
                      </span>
                    )}
                  </div>
                  <CardTitle className="font-heading text-xl mt-3">{c.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600 leading-relaxed">{c.description}</p>
                  {c.video_url && (
                    <a href={c.video_url} target="_blank" rel="noreferrer">
                      <Button className="mt-4 bg-slate-950 hover:bg-slate-800 text-white" size="sm">
                        Watch Now
                      </Button>
                    </a>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>
      <SiteFooter />
    </div>
  );
}
